import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getRaffle, registerRaffle } from '../api';
import { getStoredEmail, getStoredUser } from '../user';

const orange = '#f9b000';
const panel = { background: '#1a1a1a', border: '1px solid #333', borderRadius: 10,
  padding: 'clamp(18px, 5vw, 32px)', maxWidth: 520, margin: '24px auto', boxSizing: 'border-box' };
const field = { display: 'block', width: '100%', boxSizing: 'border-box', padding: 12,
  border: '1px solid #555', borderRadius: 6, background: '#101010', color: 'white',
  fontSize: 16, margin: '6px 0 18px' };

const timedOut = error => error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT';

const errorText = (error, fallback, submission = false) => {
  if (timedOut(error)) return submission
    ? 'Request timed out. Your entry may have been received. Try again with the same name and email; this does not add another chance.'
    : 'Loading timed out. Please try again.';
  if (submission && error?.response?.status === 429) {
    const raw = error.response.headers?.['retry-after'];
    const seconds = Number(raw);
    return Number.isFinite(seconds) && seconds > 0
      ? `Too many registrations right now. Please retry in about ${Math.ceil(seconds)} seconds.`
      : 'Too many registrations right now. Please try again shortly.';
  }
  if (!error?.response) return 'Connection problem. Please try again.';
  return error.response.data?.error || fallback;
};

// Router param changes remount the form, so an old raffle's pending state and result
// cannot bleed into a new raffle. The request-generation guards below also protect
// against mocks/transports that resolve *after* AbortController cancellation.
export default function RaffleEntryPage() {
  const { id } = useParams();
  return <RaffleForm key={id} id={id} />;
}

function RaffleForm({ id }) {
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [name, setName] = useState(() => getStoredUser());
  const [email, setEmail] = useState(() => getStoredEmail());
  const [pending, setPending] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const mounted = useRef(false);
  const submitGeneration = useRef(0);
  const submitAbort = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      submitGeneration.current += 1;
      submitAbort.current?.abort();
      submitAbort.current = null;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setRaffle(null);
    setLoading(true);
    setLoadError('');
    const load = async () => {
      try {
        const result = await getRaffle(id, { signal: controller.signal });
        if (!controller.signal.aborted && mounted.current) setRaffle(result);
      } catch (error) {
        if (!controller.signal.aborted && mounted.current) setLoadError(errorText(error, 'Unable to load raffle.'));
      } finally {
        if (!controller.signal.aborted && mounted.current) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [id, loadAttempt]);

  const retryLoad = () => setLoadAttempt(attempt => attempt + 1);

  const submit = async event => {
    event.preventDefault();
    if (pending || confirmed || submitAbort.current) return;
    const controller = new AbortController();
    const generation = ++submitGeneration.current;
    submitAbort.current = controller;
    const isCurrent = () => mounted.current && !controller.signal.aborted && submitGeneration.current === generation;
    setPending(true);
    setSubmitError('');
    try {
      await registerRaffle(id, { name: name.trim(), email: email.trim() }, { signal: controller.signal });
      if (isCurrent()) setConfirmed(true);
    } catch (error) {
      if (!isCurrent()) return;
      if (error?.response?.status === 409 && error.response.data?.error === 'Entries are not open') {
        // A late close is only applied to the originating raffle.
        setRaffle(current => current ? { ...current, state: 'closed' } : current);
      }
      setSubmitError(errorText(error, 'Could not register. Please try again.', true));
    } finally {
      if (isCurrent()) {
        submitAbort.current = null;
        setPending(false);
      }
    }
  };

  return (
    <section style={panel} aria-labelledby="raffle-title">
      <p style={{ color: orange, fontSize: 12, textTransform: 'uppercase', letterSpacing: 2 }}>Bryggeriet Ångkvarn · Event raffle</p>
      {loading ? <p role="status">Loading raffle…</p> : loadError ? (
        <div role="alert"><p>{loadError}</p><button type="button" onClick={retryLoad}>Try again</button></div>
      ) : raffle ? (
        <>
          <h1 id="raffle-title" style={{ fontSize: 'clamp(23px, 6vw, 32px)', overflowWrap: 'anywhere' }}>{raffle.title}</h1>
          {raffle.description && <p style={{ lineHeight: 1.6, overflowWrap: 'anywhere' }}>{raffle.description}</p>}
          {raffle.prize && <p style={{ color: orange, overflowWrap: 'anywhere' }}>Prize: {raffle.prize}</p>}
          {confirmed ? (
            <div role="status" style={{ border: `1px solid ${orange}`, borderRadius: 8, padding: 16 }}>
              <strong>Registration received!</strong>
              <p>You have one chance in this raffle. A repeat submission with the same email does not add chances.</p>
            </div>
          ) : raffle.state !== 'open' ? (
            <p role="status">{raffle.state === 'draft' ? 'Entries are not open yet.' : 'Entries are closed.'}</p>
          ) : (
            <form onSubmit={submit}>
              <p>Enter your name and email for one chance to win. Entry is free.</p>
              <label htmlFor="raffle-name">Display name</label>
              <input id="raffle-name" name="name" type="text" autoComplete="name" value={name}
                maxLength={100} required disabled={pending} style={field} onChange={event => setName(event.target.value)} />
              <label htmlFor="raffle-email">Email</label>
              <input id="raffle-email" name="email" type="email" inputMode="email" autoComplete="email" value={email}
                maxLength={254} required disabled={pending} style={field} onChange={event => setEmail(event.target.value)} />
              <p style={{ color: '#bbb', fontSize: 13, lineHeight: 1.6 }}>
                Your email is used only for raffle registration, duplicate prevention and winner contact;
                it is not published or used to subscribe you to marketing. Your display name may
                appear on the venue screen if you win. <Link to="/privacy" style={{ color: orange }}>Privacy information</Link>.
              </p>
              {submitError && <p role="alert" style={{ color: '#ffb4a7' }}>{submitError}</p>}
              <button type="submit" disabled={pending} style={{ background: orange, border: 0, borderRadius: 6,
                padding: '13px 20px', color: '#161616', fontWeight: 700, fontSize: 16, cursor: pending ? 'wait' : 'pointer',
                width: '100%' }}>{pending ? 'Registering…' : 'Enter raffle'}</button>
            </form>
          )}
        </>
      ) : <p>Raffle not found.</p>}
    </section>
  );
}
